/**
 * Enterprise UI Refinement — "Add depth & polish" pass. MUI's default
 * 25-level `shadows` ramp is pure black (`rgba(0,0,0,...)`) and reads
 * heavy against this app's warm palette; every Dialog/Menu/Popover/
 * Snackbar that doesn't get its own `styleOverrides` (none currently
 * do — see `components.ts`) inherits this ramp straight from MUI, so
 * replacing it here is the one edit that reaches all of them.
 *
 * Reuses the warm-dark tint `rgba(20, 16, 10, …)` `WidgetShell.tsx`
 * already hand-picked for its own hover shadow — same tint, generated
 * as a full ramp instead of one hardcoded value, so the whole app now
 * shares one soft-shadow language instead of one file inventing its
 * own.
 */
import type { Theme } from '@mui/material/styles';

const TINT = '20, 16, 10';

function createShadow(elevation: number): string {
  if (elevation === 0) return 'none';
  const y1 = Math.round(elevation * 0.6);
  const blur1 = Math.round(elevation * 1.2);
  const y2 = Math.round(elevation * 0.35);
  const blur2 = Math.round(elevation * 0.9);
  const spread2 = -Math.round(elevation * 0.15);
  const alpha1 = Math.min(0.1, 0.02 + elevation * 0.003);
  const alpha2 = Math.min(0.06, 0.015 + elevation * 0.0018);
  return `0 ${y1}px ${blur1}px rgba(${TINT}, ${alpha1.toFixed(3)}), 0 ${y2}px ${blur2}px ${spread2}px rgba(${TINT}, ${alpha2.toFixed(3)})`;
}

export const softShadows = Array.from({ length: 25 }, (_, elevation) => createShadow(elevation)) as Theme['shadows'];
